import { SimpleUnchangedProps, addEffect, flowMax } from 'ad-hok'

import { Api, addShopperUser } from '../api'
import { ProductDiscoverySource } from '../api/types'
import { LoginTrigger } from './auth'

export type Category = 'media' | 'edit' | 'product' | 'profile' | 'general'

export type Action =
  | 'bookmark'
  | 'share'
  | 'shop'
  | 'show_rodeo'
  | 'media_view'
  | 'product_view'
  | 'navigate'
  | 'site_visit'
  | 'follow'
  | 'show_behind_the_gate'
  | 'user_list_view'
  | 'search'
  | 'search_result_click'
  | 'join_creator_waitlist_from_notification'
  | 'join_creator_waitlist_notification_time_out'
  | 'join_app_waitlist_from_notification'
  | 'join_app_waitlist_notification_time_out'
  | 'login'
  | 'show_more_products'
  | 'featured_brand_click'

export interface Options {
  category: Category
  action: Action
}

const gtag: any = (window as any).gtag

export const trackEvent = (options: Options, params?: Record<string, any>) => {
  gtag('event', options.action, {
    category: options.category,
    ...(params ?? {}),
  })
}

export const setAnalyticsUserIdOnChange: SimpleUnchangedProps = flowMax(
  addShopperUser,
  addEffect(
    ({ user }) =>
      () => {
        gtag('set', {
          user_id: user?.id,
        })
      },
    ['user']
  )
)

export const trackMediaView = (
  mediaId: number,
  creatorId: number,
  api: Api
) => {
  trackEvent(
    {
      category: 'media',
      action: 'media_view',
    },
    {
      media_id: mediaId,
      creator_id: creatorId,
    }
  )

  api.analytics.createMediaView({ mediaId })
}

interface ProductViewOptions {
  productId: number
  mediaId?: number
  userListId?: number
  creatorId?: number
  discoverySource?: ProductDiscoverySource
}

export const trackProductView = (
  {
    productId,
    mediaId,
    userListId,
    creatorId,
    discoverySource,
  }: ProductViewOptions,
  api: Api
) => {
  trackEvent(
    {
      category: 'product',
      action: 'product_view',
    },
    {
      product_id: productId,
      media_id: mediaId,
      user_list_id: userListId,
      creator_id: creatorId,
      discovery_source: discoverySource,
    }
  )

  api.analytics.createProductView({
    productId,
    mediaId,
    userListId,
    discoverySource,
  })
}

export const trackBehindTheGateView = (creatorId: number, api: Api) => {
  api.analytics.createBehindTheGateView({ creatorId })
}

export const trackUserListView = (userListId: number, api: Api) => {
  trackEvent(
    {
      category: 'edit',
      action: 'user_list_view',
    },
    {
      user_list_id: userListId,
    }
  )
  api.analytics.createUserListView({ userListId })
}

export const trackEditFeedView = (editId: number, api: Api) => {
  api.analytics.createEditFeedView({ editId })
}

export const trackLoginTrigger = (loginTrigger: LoginTrigger) => {
  trackEvent(
    {
      category: 'general',
      action: 'login',
    },
    {
      login_trigger: loginTrigger,
    }
  )
}

export const trackFeaturedBrandClick = (brandId: number) => {
  trackEvent(
    {
      category: 'general',
      action: 'featured_brand_click',
    },
    {
      brand_id: brandId,
    }
  )
}
